import { utils, LitElement, html, broadcastReceiver } from '@hh/global.js'
import { gsap } from '@hh/gsap.js'

const {
  serializeForm,
  getCart,
  addToCart,
  parseLineProps,
  reRenderShopifySections
} = utils

class HHProductForm extends LitElement {
  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    this.form = this.querySelector('form')
    this.form.setAttribute('action', 'javascript:void(0)')
    this.quanityInput = this.querySelector('[name="quantity"]')
    this.submitButton = this.querySelector('[type="submit"]')

    this.receiver = broadcastReceiver('quantity', (...args) => {
      this.getQuantityFromDocument(...args)
      this.updateBtnQty(...args)
    })

    this.handlerSubmit = this.onSubmitHandler.bind(this)
    this.form.addEventListener('submit', this.handlerSubmit)
    this.addEventListener('addToCart', this.handlerSubmit)
    window.addEventListener('cart:changed', this.onCartUpdate.bind(this))
    window.addEventListener('variant:change', this.onVariantChange.bind(this))
    this.bindGiftCardFormToggle()

    const translationScript = document.getElementById('giftCardTranslations')
    if (translationScript) {
      this.translations = JSON.parse(translationScript.textContent)
    } else {
      this.translations = {}
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()

    this.removeEventListener('addToCart', this.handlerSubmit)
    window.removeEventListener('cart:changed', this.onCartUpdate)
  }

  onVariantChange(e) {
    this.form = this.querySelector('form')
    this.setLineProperties(e.detail)
  }

  bindGiftCardFormToggle() {
    const consentCheckbox = this.form.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]')
    const giftCardFields = this.form.querySelector('.js-gift-card-fields')

    if (consentCheckbox && giftCardFields) {
      const show = () => {
        gsap.killTweensOf(giftCardFields)
        giftCardFields.style.display = 'block'
        giftCardFields.style.overflow = 'hidden'

        gsap.fromTo(giftCardFields, {
          height: 0,
          opacity: 0
        }, {
          height: 'auto',
          opacity: 1,
          duration: 0.4,
          ease: 'power2.out',
          clearProps: 'height',
          onComplete: () => {
            giftCardFields.style.overflow = 'visible'
          }
        })
      }

      const hide = () => {
        gsap.killTweensOf(giftCardFields)
        giftCardFields.style.overflow = 'hidden'

        gsap.to(giftCardFields, {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: 'power2.in',
          onComplete: () => {
            giftCardFields.style.display = 'none'
          }
        })
      }

      const toggleGiftCardFields = () => {
        if (consentCheckbox.checked) {
          show()
        } else {
          hide()
        }
      }

      consentCheckbox.addEventListener('change', toggleGiftCardFields)

      if (consentCheckbox.checked) {
        giftCardFields.style.display = 'block'
      } else {
        giftCardFields.style.display = 'none'
      }
    }
  }

  getQuantityFromDocument(data) {
    if (this.quanityInput) {
      this.quanityInput.value = data.value || 1
    } else if (document.querySelector('.js-pdp-quantity-input')) {
      const quantityWrapper = document.querySelector('.js-pdp-quantity-input')
      const quantityInput = quantityWrapper?.shadowRoot.querySelector('input')
      this.quanityInput.value = quantityInput?.value ?? 1
    }
  }

  onCartUpdate(event) {
    const { cart } = event.detail
    const form = this.querySelector('.js-product-form')
    const params = JSON.parse(serializeForm(form))
    let { max } = form.dataset

    const qtyInCart = cart.items.filter(item => item.variant_id === +params.id)
      .map(item => item.quantity)
      .reduce((a, b) => a + b, 0)

    if (typeof max === 'undefined') return;

    max -= qtyInCart

    this.querySelector('hh-quantity-input').setAttribute('max', Math.max(max, 1))
    this.querySelector('[name="quantity"]').value = 1
    this.querySelector('hh-quantity-input')._value = 1

    if (max === 0) {
      this.querySelector('[type="submit"]').setAttribute('disabled', true)
      this.querySelector('.js-pdp-quantity-message').classList.remove('hidden')
    } else {
      this.querySelector('[type="submit"]').removeAttribute('disabled')
      this.querySelector('.js-pdp-quantity-message').classList.add('hidden')
    }
  }

  updateBtnQty(data) {
    const buttonEl = this.querySelector('.js-single-atc')
    const stickyATCButton = document.querySelector('.js-sticky-atc')
    const unitPrice = buttonEl?.dataset.price;
    const totalPrice = unitPrice * (data.value || 1)
    let buttonText = buttonEl.text
    const toKeep = buttonText.split(' - ')[0]
    let formattedPrice = HH.formatMoney(totalPrice, window.shopSettings.money_with_currency_format, true)
    buttonText = `${toKeep} - ${formattedPrice}`
    buttonEl.text = buttonText

    if (stickyATCButton?.buttonContent) {
      stickyATCButton.buttonContent = buttonText
    }

    if (!this.querySelector('hh-quantity-input').hasAttribute('max')) return

    const max = parseInt(this.querySelector('hh-quantity-input').getAttribute('max') || 0)

    if ((data.value || 1) >= max) {
      this.querySelector('.js-pdp-quantity-message').classList.remove('hidden')
    } else {
      this.querySelector('.js-pdp-quantity-message').classList.add('hidden')
    }
  }

  get isBundle() {
    return this.dataset.type === 'bundle'
  }

  randomFixedInteger(length) {
    return Math.floor(
      Math.pow(10, length - 1) +
      Math.random() * (Math.pow(10, length) - Math.pow(10, length - 1) - 1)
    )
  }

  setLineProperties(data) {
    const lineProperties = data?.metafields?.lineProperties

    if (!this.querySelector('.js-variant-properties')) return

    if (!!lineProperties) {
      this.querySelector('.js-variant-properties').removeAttribute('disabled')
      this.querySelector('.js-variant-properties').value = JSON.stringify(lineProperties)
    } else {
      this.querySelector('.js-variant-properties').setAttribute('disabled', '')
      this.querySelector('.js-variant-properties').value = ''
    }
  }

  validateGiftForm() {
    const form = this.form
    const consentCheckbox = form.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"')

    if (!consentCheckbox) {
      return true
    }

    if (!consentCheckbox.checked) {
      return true
    }

    const emailInput = form.querySelector('[name="properties[Recipient email]"]')
    const dateInput = form.querySelector('[name="properties[Send on]"]')
    let isValid = true

    const removeErrors = () => {
      document.querySelectorAll('.gift-form-error').forEach(error => {
        error.remove()
      })

      form.querySelectorAll('.product__gift-card__input').forEach(input => {
        input.classList.remove('error')
      })
    }
    const addError = (input, message) => {
      const wrapper = input.closest('.product__gift-card__input')
      wrapper.classList.add('error')
      const error = document.createElement('p')
      error.textContent = message
      error.className = 'gift-form-error body-x-small color-red mb-sm'
      wrapper.insertAdjacentElement('afterend', error)
      isValid = false
    }

    removeErrors()

    const email = emailInput.value.trim()
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      addError(emailInput, this.translations.gift_card_email_required || 'Recipient email is required.')
    } else if (!emailPattern.test(email)) {
      addError(emailInput, this.translations.gift_card_email_invalid || 'Invalid email format.')
    }
    if (dateInput?.value) {
      const today = new Date().toISOString().split('T')[0]
      if (dateInput.value < today) {
        addError(dateInput, this.translations.gift_card_date_invalid || 'Date must be today or later.')
      }
    }

    return isValid
  }

  async onSubmitHandler(event) {
    event.preventDefault()
    event.stopImmediatePropagation();

    if (!this.validateGiftForm()) {
      return
    }

    const { target } = event
    let submitButton = this.submitButton

    if (target !== this.form) {
      submitButton = target
    }

    submitButton.setAttribute('disabled', true)
    submitButton.classList.add('loading')

    let cart = await getCart()
    const { items } = cart

    if (this.isBundle) {
      const parent_id = Number(this.dataset.parentVariantId) || 0
      const metafields = this.getMetafieldVariantData(parent_id)
      let bxgy_id = null

      if (metafields && metafields.bxgyGifts !== null) {
        const existingParent = cart.items.find(item =>
          item.variant_id === parent_id && item.properties?.['_bxgy-id'])
          bxgy_id = existingParent?.properties['_bxgy-id'] || String(this.randomFixedInteger(14))
      }

      const items = Array.from({ length: Number(this.quanityInput.value) || 1 }).flatMap(i => {
        const itemKey = this.randomFixedInteger(14)

        const picker = document.querySelector('hh-bundle-items-picker')
        const parentLineProps = parseLineProps(picker?.dataset.parentLineProperties)
        const idInputs = Array.from(document.querySelectorAll('.js-bundle-item-id')).filter((item) => item.value && item.dataset.unselected !== 'true')
        const hasIndexOne = idInputs.some((item) => item.dataset.index === '1')
        let parentApplied = false

        return idInputs.map(item => {
          const index = item.dataset.index
          const lineProperties = document.querySelector(`.js-bundle-item-properties[data-index="${index}"]`)?.value
          let parsedLineProps = {}
          if (index === '1') {
            parsedLineProps = Object.assign({}, parentLineProps, parseLineProps(lineProperties))
            parentApplied = true
          } else if (!hasIndexOne && !parentApplied) {
            parsedLineProps = Object.assign({}, parentLineProps, parseLineProps(lineProperties))
            parentApplied = true
          }
          return {
            id: Number(item.value),
            quantity: 1,
            properties: Object.assign({
              '_is-bundle': 'true',
              '_attached-item-key': itemKey,
              '_bundle-id': itemKey,
              '_parent-id': parent_id,
              ...(bxgy_id ? { '_bxgy-id': bxgy_id } : {})
            }, parsedLineProps)
          }
        })
      })

      reRenderShopifySections(
        await addToCart({ items })
      )
    } else {
      const params = JSON.parse(serializeForm(this.form))
      const metafields = this.getMetafieldVariantData(params.id)
      
      const lineItems = []
      const consentCheckbox = this.form.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]')

      if (consentCheckbox && !consentCheckbox.checked) {
        if (params.properties) {
          delete params.properties['Recipient email']
          delete params.properties['Recipient name']
          delete params.properties['Send on']
        }
      }

      
      if (metafields.bxgyGifts !== null) {        
        const gifts = JSON.parse(window.atob(metafields.bxgyGifts))
        let addableGift = false
        if (metafields.bxgyGiftQty > 0 && metafields.bxgyReqQty > 0 && gifts.length === 1) {
          addableGift = await this.addableAutoBXGY(params, metafields, gifts[0])
        }        

        if (addableGift) {
          const bxgy_id = typeof addableGift === 'boolean' ? this.randomFixedInteger(14) : addableGift

          params['properties'] = {
            ...(params['properties'] || {}),
            '_bxgy-id': String(bxgy_id)
          }

          lineItems.push(params)

          const hasBxgy = items.some((item) => item.properties['_is-bxgy'])

          if (typeof addableGift === 'boolean' && !hasBxgy) {
            const giftVariant = gifts[0]            

            if (giftVariant.isBundle) {
              const bundleKey = this.randomFixedInteger(14)
            
              const components = [
                giftVariant.bundleComponents?.comp1?.[0],
                giftVariant.bundleComponents?.comp2?.[0],
                giftVariant.bundleComponents?.comp3?.[0]
              ].filter(Boolean)
            
              components.forEach(component => {
                lineItems.push({
                  id: component.id,
                  quantity: 1,
                  properties: {
                    "_is-bxgy": true,
                    "_bxgy-id": String(bxgy_id),
                    "_bxgy-parent": String(giftVariant.id),
                    "_is-bundle": true,
                    "_bundle-id": String(bundleKey),
                    "_attached-item-key": String(bundleKey),
                    "_parent-id": String(giftVariant.id)
                  }
                })
              })
            } else {
              lineItems.push({
                id: giftVariant.id,
                quantity: 1,
                properties: {
                  '_is-bxgy': true,
                  "_bxgy-id": String(bxgy_id),
                }
              })
            }
          }          

          reRenderShopifySections(
            await addToCart({ items: lineItems })
          )
        } else {
          reRenderShopifySections(
            await addToCart(params)
          )
        }
      } else {
        reRenderShopifySections(
          await addToCart(params)
        )
      }
    }

    submitButton.removeAttribute('disabled')
    submitButton.classList.remove('loading')

    cart = await getCart()

    document.dispatchEvent(
      new CustomEvent('cart:changed', {
        bubbles: true,
        detail: {
          cart
        }
      })
    )

    this.dispatchEvent(
      new CustomEvent('productAdded', {
        bubbles: true,
        detail: {
          cart
        }
      })
    )

    window.Klarna?.OnsiteMessaging?.refresh()

    this.openCart()
  }

  async addableAutoBXGY(params, variant, gift) {
    const cart = await getCart()
    const { items } = cart
    const active_qty = Number(params.quantity) || 1

    // info for main variant
    let bxgy_id = false,
      total_count = active_qty
    const parentItems = items.filter(item => item.variant_id === variant.id)
    parentItems.forEach(item => {
      if (item.properties['_bxgy-id']) {
        bxgy_id = item.properties['_bxgy-id']
      }
      total_count += item.quantity
    })

    // info for gifts
    let gifts_count = 0
    if (bxgy_id) {
      items.forEach(item => {
        if (item.properties['_is-bxgy'] && item.properties['_bxgy-id'] === bxgy_id) {
          gifts_count += item.quantity
        }
      })
    }

    if (
      total_count >= variant.bxgyReqQty &&
      gifts_count === 0 &&
      variant.bxgyGiftQty === 1
    ) {
      return true
    } else {
      return bxgy_id
    }
  }

  async onSubmitFromEvent(event) {
    event.preventDefault()
    const { target } = event

    target.setAttribute('disabled', true)
    target.classList.add('loading')

    reRenderShopifySections(
      await addToCart(JSON.parse(serializeForm(this.form)))
    )

    const cart = await getCart()

    target.removeAttribute('disabled')
    target.classList.remove('loading')

    document.dispatchEvent(
      new CustomEvent('cart:changed', {
        bubbles: true,
        detail: {
          cart
        }
      })
    )

    this.openCart()

    window.Klarna?.OnsiteMessaging?.refresh()
  }

  getMetafieldVariantData(variant_id) {
    this.metafieldVariantData =
      this.metafieldVariantData ||
      JSON.parse(document.querySelector('#variantMetafields').textContent)

    return this.metafieldVariantData.find(data => data['id'] === Number(variant_id))
  }

  openCart() {
    document.body.classList.add('mini-cart-is-visible')

    setTimeout(() => {
      document.querySelector('html').classList.add('overflow-hidden')
      document.body.classList.add('mini-cart-is-active')
      document.querySelector('.js-mega-menu-overlay').classList.add('active')
    });
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-product-form', HHProductForm)
